import { useMemo, useState } from "react";
import { BookOpen, Pencil, Plus, Trash2 } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import { PageHeader, SectionCard, Tabs } from "@/features/power_projects/components/ui";
import type { NormKind } from "../api";
import type { NormChange, NormRow } from "../types";
import {
  NORM_HIDDEN_KEYS,
  extractApiError,
  fmtDateTime,
  normCellText,
  normColumnLabel,
  normColumns,
} from "../utils";
import {
  useCreateNorm,
  useDeleteNorm,
  useNormChanges,
  useNorms,
  usePatchNorm,
} from "../hooks";

type CatalogTab = "lines" | "poles" | "changes";

// ── edit/create modal (schema derived from the rows themselves) ──────────────
function NormEditModal({
  kind,
  kindLabel,
  row,
  /** Existing row used to derive the field set (and value types) for CREATE. */
  template,
  onClose,
}: {
  kind: NormKind;
  kindLabel: string;
  row: NormRow | null;
  template: NormRow | null;
  onClose: () => void;
}) {
  const create = useCreateNorm(kind);
  const patch = usePatchNorm(kind);
  const pending = create.isPending || patch.isPending;
  const error = create.error ?? patch.error;

  const source = row ?? template;
  const keys = useMemo(
    () => (source ? Object.keys(source).filter((k) => !NORM_HIDDEN_KEYS.has(k)) : []),
    [source],
  );

  const [values, setValues] = useState<Record<string, string | boolean>>(() => {
    const init: Record<string, string | boolean> = {};
    for (const k of keys) {
      const v = row?.[k];
      const t = source?.[k];
      if (typeof t === "boolean") init[k] = typeof v === "boolean" ? v : false;
      else init[k] = v == null ? "" : String(v);
    }
    return init;
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: Record<string, unknown> = {};
    for (const k of keys) {
      const typeSource = source?.[k];
      const raw = values[k];
      if (typeof typeSource === "boolean") {
        payload[k] = Boolean(raw);
      } else if (typeof typeSource === "number") {
        const s = String(raw).trim();
        payload[k] = s === "" ? null : Number(s.replace(",", "."));
      } else {
        payload[k] = String(raw);
      }
    }
    const opts = { onSuccess: onClose };
    if (row) patch.mutate({ id: row.id, payload }, opts);
    else create.mutate(payload, opts);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={row ? `Edytuj wpis — ${kindLabel}` : `Nowy wpis — ${kindLabel}`}
    >
      <p className="mb-3 border border-amber-600 px-3 py-2 font-mono text-[11px] text-amber-600">
        Zmiana katalogu wpływa na przyszłe obliczenia — istniejące wyniki zachowują wersję
        katalogu z chwili obliczenia.
      </p>
      <form onSubmit={submit} className="space-y-2.5">
        {keys.map((k) => {
          const typeSource = source?.[k];
          return (
            <label key={k} className="block">
              <span className="section-label">{normColumnLabel(k)}</span>
              {typeof typeSource === "boolean" ? (
                <input
                  type="checkbox"
                  className="mt-1 block"
                  checked={Boolean(values[k])}
                  onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.checked }))}
                />
              ) : (
                <input
                  className="input-base mt-1 py-1.5 font-mono text-[12px]"
                  type={typeof typeSource === "number" ? "number" : "text"}
                  step="any"
                  value={String(values[k] ?? "")}
                  onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))}
                />
              )}
            </label>
          );
        })}
        {error != null && (
          <p className="font-mono text-xs text-[var(--color-primary)]">{extractApiError(error)}</p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Anuluj
          </Button>
          <Button type="submit" size="sm" loading={pending}>
            Zapisz
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ── one norm table (lines OR poles) ──────────────────────────────────────────
function NormTable({
  kind,
  kindLabel,
  canEdit,
}: {
  kind: NormKind;
  kindLabel: string;
  canEdit: boolean;
}) {
  const { data: rows, isLoading } = useNorms(kind);
  const del = useDeleteNorm(kind);
  const [editor, setEditor] = useState<{ row: NormRow | null } | null>(null);

  const columns = useMemo(() => normColumns(rows ?? []), [rows]);
  const template = rows?.[0] ?? null;

  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  return (
    <SectionCard
      title={kindLabel}
      bodyClassName="p-0"
      action={
        canEdit ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={!template}
            title={
              template
                ? undefined
                : "Dodawanie wymaga znanego schematu — katalog nie ma jeszcze żadnego wpisu"
            }
            onClick={() => setEditor({ row: null })}
          >
            <Plus size={13} className="mr-1" /> Dodaj wpis
          </Button>
        ) : undefined
      }
    >
      {!rows?.length ? (
        <p className="py-8 text-center font-mono text-xs text-[var(--color-text-muted)]">
          Katalog jest pusty.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] text-left">
                {columns.map((c) => (
                  <th key={c} className="section-label whitespace-nowrap px-3 py-2">
                    {normColumnLabel(c)}
                  </th>
                ))}
                {canEdit && <th className="w-16 px-3 py-2" />}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-[var(--color-border-muted)] last:border-0 hover:bg-[var(--color-bg-elevated)]"
                >
                  {columns.map((c) => (
                    <td key={c} className="whitespace-nowrap px-3 py-1.5 font-mono text-xs">
                      {normCellText(row[c])}
                    </td>
                  ))}
                  {canEdit && (
                    <td className="px-3 py-1.5">
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          title="Edytuj"
                          onClick={() => setEditor({ row })}
                          className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          type="button"
                          title="Usuń"
                          onClick={() => {
                            if (window.confirm("Usunąć ten wpis z katalogu norm?"))
                              del.mutate(row.id, {
                                onError: (err) => window.alert(extractApiError(err)),
                              });
                          }}
                          className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editor && (
        <NormEditModal
          kind={kind}
          kindLabel={kindLabel}
          row={editor.row}
          template={template}
          onClose={() => setEditor(null)}
        />
      )}
    </SectionCard>
  );
}

// ── history of catalog changes ───────────────────────────────────────────────
const ENTRY_KIND_LABEL: Record<string, string> = { line: "Przewód", pole: "Słup" };

/** Static cheat sheet (public/docs) — every "Konstrukcja" value the module accepts, with schematics. */
const POLE_CATALOG_URL = "/docs/katalog_konstrukcji_slupow_DLC10.pdf";

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

/** Compact "field: old → new" diff of an edit (skips ids/timestamps). */
function changeDiff(c: NormChange): { key: string; from: unknown; to: unknown }[] {
  const before = asRecord(c.before);
  const after = asRecord(c.after);
  if (!before || !after) return [];
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  const out: { key: string; from: unknown; to: unknown }[] = [];
  for (const k of keys) {
    if (NORM_HIDDEN_KEYS.has(k)) continue;
    if (JSON.stringify(before[k]) !== JSON.stringify(after[k])) {
      out.push({ key: k, from: before[k], to: after[k] });
    }
  }
  return out;
}

function ChangesTab() {
  const { data: changes, isLoading } = useNormChanges();
  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }
  if (!changes?.length) {
    return (
      <p className="py-8 text-center font-mono text-xs text-[var(--color-text-muted)]">
        Brak zarejestrowanych zmian katalogu.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      {changes.map((c, i) => {
        const action = c.before == null ? "Dodanie" : c.after == null ? "Usunięcie" : "Edycja";
        const kind =
          typeof c.entry_kind === "string" ? (ENTRY_KIND_LABEL[c.entry_kind] ?? c.entry_kind) : "";
        const diff = changeDiff(c);
        return (
          <div key={String(c.id ?? i)} className="card p-3">
            <div className="mb-1 flex flex-wrap items-baseline gap-2">
              <span className="font-mono text-xs font-bold">
                {fmtDateTime(typeof c.created_at === "string" ? c.created_at : null)}
              </span>
              {typeof c.changed_by_name === "string" && c.changed_by_name && (
                <span className="font-mono text-[11px] text-[var(--color-text-secondary)]">
                  {c.changed_by_name}
                </span>
              )}
              <span className="border border-[var(--color-border)] px-1.5 py-0.5 font-mono text-[10px] uppercase text-[var(--color-text-secondary)]">
                {action}
                {kind ? ` · ${kind}` : ""}
              </span>
              <span className="font-mono text-[11px] font-bold">{normCellText(c.entry_name)}</span>
              {c.version != null && (
                <span className="ml-auto font-mono text-[10px] text-[var(--color-text-tertiary)]">
                  wersja katalogu: {normCellText(c.version)}
                </span>
              )}
            </div>
            {diff.length > 0 && (
              <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                {diff.map((d) => (
                  <span key={d.key} className="font-mono text-[11px] text-[var(--color-text-secondary)]">
                    <span className="text-[var(--color-text-tertiary)]">{normColumnLabel(d.key)}:</span>{" "}
                    {normCellText(d.from)} → <b>{normCellText(d.to)}</b>
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Katalog norm (przewody + słupy) z historią zmian. Cała strona jest za
 * TechCalcNormsGuard (uprawnienie daje podgląd i edycję), więc canEdit służy
 * tylko jako zabezpieczenie na wypadek przestarzałej sesji. */
export function NormCatalogPage() {
  const user = useAuthStore((s) => s.user);
  const canEdit = !!user?.has_tech_calc_norms_permission;
  const [tab, setTab] = useState<CatalogTab>("lines");

  return (
    <div>
      <PageHeader
        title="Katalog norm — obliczenia techniczne"
        subtitle="Parametry przewodów i słupów używane w obliczeniach sił"
        actions={
          <Button
            size="sm"
            variant="secondary"
            title="Katalog konstrukcji słupów DLC_10 (PDF, otwiera się w nowej karcie)"
            onClick={() => window.open(POLE_CATALOG_URL, "_blank", "noopener,noreferrer")}
          >
            <BookOpen size={14} className="mr-1" /> Przykłady słupów
          </Button>
        }
      />

      {canEdit ? (
        <p className="mb-4 border border-amber-600 px-3 py-2 font-mono text-[11px] text-amber-600">
          Zmiana katalogu wpływa na przyszłe obliczenia — istniejące wyniki zachowują wersję
          katalogu z chwili obliczenia.
        </p>
      ) : (
        <p className="mb-4 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 font-mono text-[11px] text-[var(--color-text-secondary)]">
          Katalog jest w trybie tylko do odczytu — edycja wymaga uprawnienia do zarządzania
          katalogiem norm.
        </p>
      )}

      <Tabs<CatalogTab>
        tabs={[
          { key: "lines", label: "Przewody" },
          { key: "poles", label: "Słupy" },
          { key: "changes", label: "Historia zmian" },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === "lines" && <NormTable kind="lines" kindLabel="Przewody" canEdit={canEdit} />}
      {tab === "poles" && <NormTable kind="poles" kindLabel="Słupy" canEdit={canEdit} />}
      {tab === "changes" && <ChangesTab />}
    </div>
  );
}
